/**
 * Media Library API — all Cloudflare work goes through our backend. Client
 * sends multipart files to `/api/media-library/upload`; the backend pushes
 * to Cloudflare (Images or Stream) and creates the library row in one call.
 * No API keys, no upload URLs, and no Cloudflare requests leak into the
 * browser.
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";
import { resizeImageFile } from "./resizeImage";

function authHeaders(accessToken) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return headers;
}

/**
 * Cloudflare Images only accepts these content types — error code 5455.
 * Anything else (AVIF, HEIC, BMP, TIFF, ...) must be re-encoded in the
 * browser before the multipart upload leaves the client.
 */
const CLOUDFLARE_ALLOWED_IMAGE_MIMES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/svg+xml",
]);

async function ensureCloudflareCompatibleImage(file) {
    if (!file || CLOUDFLARE_ALLOWED_IMAGE_MIMES.has(file.type)) return file;

    let bitmap = null;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        bitmap = await decodeViaImgElement(file);
    }
    if (!bitmap) {
        throw new Error(`Couldn't decode ${file.type || "image"} in this browser. Try JPEG, PNG, WebP, or GIF.`);
    }

    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0);
    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))),
            "image/jpeg",
            0.92,
        );
    });

    const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

function decodeViaImgElement(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
}

/**
 * Send a File to the backend via multipart; backend forwards to Cloudflare
 * and returns the saved library row. `onProgress` receives 0..1 while the
 * request body is still being uploaded to our server.
 */
function postFileToLibrary({ file, title, tags, accessToken, onProgress }) {
    return new Promise((resolve) => {
        try {
            const form = new FormData();
            form.append("file", file, file.name || "upload");
            if (title) form.append("title", title);
            if (tags && Array.isArray(tags)) form.append("tags", JSON.stringify(tags));

            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${getApiBaseUrl()}/api/media-library/upload`);
            // FormData manages its own Content-Type; set everything else.
            const headers = authHeaders(accessToken);
            for (const [k, v] of Object.entries(headers)) {
                if (k.toLowerCase() === "content-type") continue;
                xhr.setRequestHeader(k, v);
            }
            if (onProgress && xhr.upload) {
                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) onProgress(e.loaded / e.total);
                };
            }
            xhr.onload = () => {
                let json = {};
                try { json = JSON.parse(xhr.responseText || "{}"); } catch { /* ignore */ }
                if (xhr.status >= 200 && xhr.status < 300 && json.success === true && json.data?.item) {
                    resolve({ ok: true, data: json.data.item });
                } else {
                    resolve({ ok: false, error: json?.error?.message || `Upload failed (${xhr.status})` });
                }
            };
            xhr.onerror = () => resolve({ ok: false, error: "Network error during upload" });
            xhr.send(form);
        } catch (err) {
            resolve({ ok: false, error: err instanceof Error ? err.message : "Upload failed" });
        }
    });
}

/** Register an already-hosted asset in the library (e.g. Giphy URL). */
export async function createLibraryItem({ accessToken, ...body } = {}) {
    const res = await fetch(`${getApiBaseUrl()}/api/media-library`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data?.item) {
        return { ok: true, data: json.data.item };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

export async function listLibraryItems({ type, q, limit, offset, accessToken } = {}) {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (q) params.set("q", q);
    if (limit !== undefined) params.set("limit", String(limit));
    if (offset !== undefined) params.set("offset", String(offset));
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`${getApiBaseUrl()}/api/media-library${qs}`, {
        method: "GET",
        headers: authHeaders(accessToken),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

export async function updateLibraryItem({ id, title, tags, accessToken } = {}) {
    const body = {};
    if (title !== undefined) body.title = title;
    if (tags !== undefined) body.tags = tags;
    const res = await fetch(`${getApiBaseUrl()}/api/media-library/${id}`, {
        method: "PATCH",
        headers: authHeaders(accessToken),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data?.item) {
        return { ok: true, data: json.data.item };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

export async function deleteLibraryItem({ id, accessToken } = {}) {
    const res = await fetch(`${getApiBaseUrl()}/api/media-library/${id}`, {
        method: "DELETE",
        headers: authHeaders(accessToken),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) return { ok: true };
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

export async function bumpLibraryItemUsage({ id, accessToken } = {}) {
    const res = await fetch(`${getApiBaseUrl()}/api/media-library/${id}/usage`, {
        method: "POST",
        headers: authHeaders(accessToken),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) return { ok: true };
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * One-call image upload: canvas-converts unsupported formats (AVIF/BMP/etc.)
 * to JPEG, then POSTs multipart to the backend which pushes to Cloudflare
 * Images and creates the library row. Returns the saved library item.
 */
export async function uploadImageToLibrary({ file, title, tags, accessToken, onProgress } = {}) {
    // Resize first (middle step), then ensure the result is Cloudflare-compatible.
    const resizedFile = await resizeImageFile(file);
    let fileToUpload;
    try {
        fileToUpload = await ensureCloudflareCompatibleImage(resizedFile);
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : "Image conversion failed" };
    }
    const titleToSend = title ?? (file?.name ? file.name.replace(/\.[^.]+$/, "") : null);
    return postFileToLibrary({ file: fileToUpload, title: titleToSend, tags, accessToken, onProgress });
}

/**
 * One-call video upload via the backend proxy. Max 200MB per the backend
 * multer cap (use /api/media/upload/stream-tus for larger/resumable uploads).
 */
export async function uploadVideoToLibrary({ file, title, tags, accessToken, onProgress } = {}) {
    const titleToSend = title ?? (file?.name ? file.name.replace(/\.[^.]+$/, "") : null);
    return postFileToLibrary({ file, title: titleToSend, tags, accessToken, onProgress });
}
